import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";

const MENU_WIDTH = 240;
const ANCHOR_GAP = 4;
const VIEWPORT_MARGIN = 8;

export const KebabIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

export const MenuItem = ({ icon, children, onClick, danger }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full min-h-[36px] px-3 py-[6px] rounded-[8px] flex items-center gap-3 text-left cursor-pointer hover:bg-[#F8F9FE] ${
      danger ? "text-[#EF4444]" : "text-[#1A2332]"
    }`}
  >
    {icon && (
      <span
        className={`w-4 h-4 shrink-0 flex items-center justify-center ${
          danger ? "text-[#EF4444]" : "text-[#7C829A]"
        }`}
      >
        {icon}
      </span>
    )}
    <span className="flex-1 min-w-0 truncate font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize">
      {children}
    </span>
  </button>
);

// Kebab dropdown rendered into document.body so the section cards'
// overflow-hidden shells and the scrolling row lists can never clip it.
// `anchorEl` is the trigger button element; the menu hangs from its
// bottom-right corner and flips above it when it would leave the viewport.
export function DropdownMenu({ anchorEl, onClose, children }) {
  const menuRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!anchorEl || !menuRef.current) return undefined;

    const rect = anchorEl.getBoundingClientRect();
    const menuHeight = menuRef.current.offsetHeight;
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN),
    );
    let top = rect.bottom + ANCHOR_GAP;
    if (top + menuHeight > window.innerHeight - VIEWPORT_MARGIN) {
      top = Math.max(VIEWPORT_MARGIN, rect.top - menuHeight - ANCHOR_GAP);
    }
    setPosition({ top, left });

    // The menu is fixed-positioned, so any scroll/resize detaches it from
    // its row — close instead of chasing the anchor around.
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [anchorEl, onClose]);

  if (!anchorEl) return null;

  return createPortal(
    <OutsideClickHandler
      onOutsideClick={(event) => {
        // The trigger's own click already toggles the menu closed.
        if (anchorEl.contains(event.target)) return;
        onClose();
      }}
    >
      <div
        ref={menuRef}
        className="fixed z-50 w-[240px] p-1 flex flex-col gap-[2px] bg-white rounded-[16px] shadow-[0px_2px_8px_rgba(0,0,0,0.08),inset_0px_0px_1px_rgba(0,0,0,0.30)]"
        style={position ? { top: position.top, left: position.left } : { top: 0, left: 0, visibility: "hidden" }}
      >
        {children}
      </div>
    </OutsideClickHandler>,
    document.body,
  );
}

export default DropdownMenu;
